import { type z } from 'zod';

import { api } from '@/api/client';
import { type AdapterFn } from '@/lib/adapters/httpAdapter.types';
import { resolveUrl } from '@/lib/formatters';
import { normalizeError } from '@/lib/transformers/normalizeError';
import { parseResponse } from '@/lib/validators/parseResponse';

type HttpGetAdapterOptions<TResponse, TParams> = {
  url: string | ((params: TParams) => string);
  schema: z.ZodType<TResponse>;
  mapParams?: (params: TParams) => Record<string, unknown>;
  /**
   * Set to false when the schema validates the full envelope (e.g. `{ data, pageable }`)
   * instead of just the `data` payload. Defaults to true.
   */
  unwrap?: boolean;
};

export function httpGetAdapter<TResponse, TParams = void>(
  options: HttpGetAdapterOptions<TResponse, TParams>
): AdapterFn<TParams, TResponse> {
  const { url, schema, mapParams, unwrap = true } = options;

  return (async (params?: TParams) => {
    try {
      const res = await api.get(resolveUrl(url, params), {
        params: mapParams && params ? mapParams(params) : undefined,
      });

      return parseResponse(res, schema, unwrap);
    } catch (error) {
      return normalizeError(error);
    }
  }) as AdapterFn<TParams, TResponse>;
}
